import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import ErrorMessage from '../../../Elements/ErrorMessage';
import LeadService from '../../../Services/leadService';
import LeadImportParameters from './LeadImportParameters';

export default class ImportLeadsWindowDescription {
  static titleForCreate = 'Import Leads';
  static finishButtonLabel = 'Start Import';
  // LeadImportParameters's constructor self-populates UserId/OfficeId/AssignedToUserId from the
  // logged-in user (via AuthService), so the wizard's default `new itemType()` seeding is sufficient here.
  static itemType = LeadImportParameters;
  // Replaces the old ImportLeadsPanel host success message (ErrorMessage.SetInfo on leadImportId > 0).
  // The wizard shows this as the single success message; LeadAdmin's clickOnSave only filters the table.
  static messageOnSuccess = 'Check your notifications to see if any data was skipped during the import process.';

  // Preserves the old ImportLeadsPanel.onSubmit FormData shape exactly. A corrupted upload
  // (leadImportId === 0, or no result) is mapped to a wizard error instead of a false success,
  // so it surfaces via the wizard's normal error handling instead of clickOnCancel(0).
  static serviceFunction = (edited) => {
    const data = new FormData();
    data.append('file', edited.selectedFile);
    data.append('Name', edited.Name);
    data.append('ProjectType', edited.ProjectType);
    data.append('Industry', edited.Industry);
    data.append('Description', edited.Description);
    data.append('SourceCode', edited.SourceCode);
    data.append('SourceName', edited.SourceName);
    data.append('AssignedToUserId', (edited.AssignedToUserId ?? '').toString());
    data.append('OfficeId', (edited.OfficeId ?? '').toString());
    data.append('UserId', (edited.UserId ?? '').toString());
    return LeadService.postLeadImportFile(data).then((result) =>
      !result || result.leadImportId === 0 ? { error: 'The uploaded file is corrupted.' } : result
    );
  };

  static pagesInGeneralEditWizzard = [
    {
      label: 'Import Leads',
      fields: [
        {
          fieldName: 'Name',
          label: 'Lead Name',
          className: 'col-6',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'ProjectType',
          label: 'Project Type',
          className: 'col-6',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'Industry',
          label: 'Industry',
          className: 'col-6',
          type: InputTypesEnum.IndustryLookupItemSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'Description',
          label: 'Description',
          className: 'col-6',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          // Restores the old panel's static template download (mirrors UploadPanelDescription's
          // DownloadTemplateLink Link field; the link text is derived from the value's filename).
          fieldName: 'DownloadTemplateLink',
          label: 'Download Template',
          className: 'col-6',
          type: InputTypesEnum.Link,
          displayHyperlink: true,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'selectedFile',
          label: 'Choose a CSV file',
          className: 'col-6',
          type: InputTypesEnum.FileUploadField,
          acceptedTypes: '.csv',
          validityChecker: ValidityHelper.forRequiredFileField,
          // Mirrors the old ImportLeadsPanel.fileUploadOnChange warning, shown once a file is picked.
          onFieldChange: (item) => {
            if (item.selectedFile) {
              ErrorMessage.SetWarning(
                'Ensure that commas are used strictly as delimiters in the CSV file. Similarly, line breaks should also only be used to indicate new rows.' +
                  ' If the data within a row contains either or both (even if it is within quotation marks), it may be misinterpreted, and skipped during import.'
              );
            }
          },
        },
        {
          fieldName: 'SourceCode',
          label: 'Source',
          className: 'col-6',
          type: InputTypesEnum.SourceLookupItemSelect,
          validityChecker: (e) => (e === '' ? 'Please, select a source!' : ''),
        },
        {
          fieldName: 'SourceName',
          label: 'Source Name',
          className: 'col-6',
          type: InputTypesEnum.Text,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'AssignedToUserId',
          label: 'Assigned To',
          // Full row: the user/office select triggers truncate their labels at half width.
          className: 'col-12',
          type: InputTypesEnum.UserSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'OfficeId',
          label: 'Office',
          className: 'col-12',
          type: InputTypesEnum.OfficeSelect,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
      ],
    },
  ];
}
